import { format, parse } from 'date-fns';
import { isNil } from 'lodash';
import { RiskItem } from 'src/app/api';
import { DeclarationState } from 'src/app/core/declaration.store';

import { ChildInfo } from 'src/app/core/offer.store';
import { RPM } from 'src/app/shared/constants/movable-heritage.constants';
import { OBJVAL } from 'src/app/shared/constants/object-value.constants';
import {
  ADR_ETAGE,
  FRANCHISE,
  HAB_TYPE,
  NATURE_LOG,
  NATURE_OCCU,
  NBR_COLOC,
  NBR_PIECES,
  NBR_PIECES_40M2,
  NOM_BAILLEUR,
  POSSEDE_PISCINE,
  TYPE_BAILLEUR,
  TYPE_CH,
} from 'src/app/shared/constants/risk-item-values-ani.constant';
import {
  Etage,
  Lessor,
  LessorType,
  NatureResidence,
  QualiteOccupant,
  SuperficieChambre,
  SurfaceDependance,
  TypeHabitation,
} from 'src/app/shared/enum/housing.enum';
import { PathType } from 'src/app/shared/enum/path-type.enum';
import { DeductibleType } from '../enum/deductible-type.enum';
import { YearHouseBuilded } from '../enum/house-builded.enum';
import { QuotationSummaryResponse } from '../models/model/concretes/shared/quotation-summary.model';
import { mapToEnumValue } from './enum-mapper.util';

const HOUSING_TYPE_VALUES = [TypeHabitation.Appartement, TypeHabitation.Maison, TypeHabitation.Autres];
const HOUSING_TYPE_ALIASES = {
  house: TypeHabitation.Maison,
  maison: TypeHabitation.Maison,
  apartment: TypeHabitation.Appartement,
  appartement: TypeHabitation.Appartement,
  flat: TypeHabitation.Appartement,
  other: TypeHabitation.Autres,
  autres: TypeHabitation.Autres,
};

const RESIDENCE_TYPE_VALUES = [
  NatureResidence.Principale,
  NatureResidence.Secondaire,
  NatureResidence.ForRent,
  NatureResidence.Etudiant,
  NatureResidence.Chambre,
  NatureResidence.ChambreCROUS,
  NatureResidence.Inconnue,
];
const RESIDENCE_TYPE_ALIASES = {
  principal: NatureResidence.Principale,
  principale: NatureResidence.Principale,
  primary: NatureResidence.Principale,
  secondary: NatureResidence.Secondaire,
  secondaire: NatureResidence.Secondaire,
  etudiant: NatureResidence.Etudiant,
  student: NatureResidence.Etudiant,
  forrent: NatureResidence.ForRent,
  pourlocation: NatureResidence.ForRent,
};

const OCCUPANT_QUALITY_VALUES = [
  QualiteOccupant.Proprietaire,
  QualiteOccupant.Locataire,
  QualiteOccupant.Colocataire,
];
const OCCUPANT_QUALITY_ALIASES = {
  owner: QualiteOccupant.Proprietaire,
  proprietaire: QualiteOccupant.Proprietaire,
  tenant: QualiteOccupant.Locataire,
  locataire: QualiteOccupant.Locataire,
  roommate: QualiteOccupant.Colocataire,
  colocataire: QualiteOccupant.Colocataire,
};

const APARTMENT_FLOOR_VALUES = [Etage.RezDeChaussee, Etage.Intermediaire, Etage.DernierEtage];
const APARTMENT_FLOOR_ALIASES = {
  groundfloor: Etage.RezDeChaussee,
  rezdechaussee: Etage.RezDeChaussee,
  middlefloor: Etage.Intermediaire,
  intermediate: Etage.Intermediaire,
  intermediaire: Etage.Intermediaire,
  topfloor: Etage.DernierEtage,
  dernieretage: Etage.DernierEtage,
};

const CONSTRUCTION_YEAR_VALUES = [
  YearHouseBuilded.BEFORE_1915,
  YearHouseBuilded.FROM_1915_TO_1948,
  YearHouseBuilded.FROM_1949_TO_1967,
  YearHouseBuilded.FROM_1968_TO_1974,
  YearHouseBuilded.FROM_1975_TO_1981,
  YearHouseBuilded.FROM_1982_TO_1989,
  YearHouseBuilded.FROM_1990_TO_1999,
  YearHouseBuilded.FROM_2000_TO_2005,
  YearHouseBuilded.FROM_2006_TO_2012,
  YearHouseBuilded.AFTER_2013,
  YearHouseBuilded.Inconnue,
];
const CONSTRUCTION_YEAR_ALIASES = {
  before1915: YearHouseBuilded.BEFORE_1915,
  between1915and1948: YearHouseBuilded.FROM_1915_TO_1948,
  entre1915et1948: YearHouseBuilded.FROM_1915_TO_1948,
  between1949and1967: YearHouseBuilded.FROM_1949_TO_1967,
  entre1949et1967: YearHouseBuilded.FROM_1949_TO_1967,
  between1968and1974: YearHouseBuilded.FROM_1968_TO_1974,
  entre1968et1974: YearHouseBuilded.FROM_1968_TO_1974,
  between1975and1981: YearHouseBuilded.FROM_1975_TO_1981,
  entre1975et1981: YearHouseBuilded.FROM_1975_TO_1981,
  between1982and1989: YearHouseBuilded.FROM_1982_TO_1989,
  entre1982et1989: YearHouseBuilded.FROM_1982_TO_1989,
  between1990and1999: YearHouseBuilded.FROM_1990_TO_1999,
  entre1990et1999: YearHouseBuilded.FROM_1990_TO_1999,
  between2000and2005: YearHouseBuilded.FROM_2000_TO_2005,
  entre2000et2005: YearHouseBuilded.FROM_2000_TO_2005,
  between2006and2012: YearHouseBuilded.FROM_2006_TO_2012,
  entre2006et2012: YearHouseBuilded.FROM_2006_TO_2012,
  after2013: YearHouseBuilded.AFTER_2013,
  unknown: YearHouseBuilded.Inconnue,
};

const DEDUCTIBLE_VALUES = [DeductibleType.CentCinquante, DeductibleType.TroisCents];
const DEDUCTIBLE_ALIASES = {
  centcinquante: DeductibleType.CentCinquante,
  troiscents: DeductibleType.TroisCents,
};

const LESSOR_VALUES = ['Private', 'Social'] as const;
const LESSOR_ALIASES = {
  prive: 'Private',
  private: 'Private',
  social: 'Social',
};

const ROOM_SIZE_VALUES = [
  SuperficieChambre.Unknown,
  SuperficieChambre.CROUSMoinsDe10m2,
  SuperficieChambre.MoinsDe10m2,
  SuperficieChambre.De11a40m2,
  SuperficieChambre.PlusDe40m2,
];

// Fonction qui génère le RiskItems avec `store` passé en paramètre
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getRiskItems(store: any): RiskItem[] {
  const declaration = store.declaration() as DeclarationState;
  const path = store.path() as string;
  const climatic =
    store.climatic().ClimaticPeril.length > 0 ? JSON.stringify(store.climatic().ClimaticPeril) : '';
  const arrayOfRiskItems: RiskItem[] = [];
  const mappedHousingType = mapToEnumValue(
    declaration.housingType,
    HOUSING_TYPE_VALUES,
    TypeHabitation.Appartement,
    HOUSING_TYPE_ALIASES,
  );
  const mappedResidenceType = mapToEnumValue(
    declaration.residenceType,
    RESIDENCE_TYPE_VALUES,
    NatureResidence.Inconnue,
    RESIDENCE_TYPE_ALIASES,
  );
  const mappedQualiteOccupant = mapToEnumValue(
    declaration.qualiteOccupant,
    OCCUPANT_QUALITY_VALUES,
    QualiteOccupant.Locataire,
    OCCUPANT_QUALITY_ALIASES,
  );
  const mappedApartmentFloor = mapToEnumValue(
    declaration.apartmentFloor,
    APARTMENT_FLOOR_VALUES,
    Etage.Intermediaire,
    APARTMENT_FLOOR_ALIASES,
  );
  const mappedConstructionYear = mapToEnumValue(
    declaration.dateHouseBuilted,
    CONSTRUCTION_YEAR_VALUES,
    YearHouseBuilded.Inconnue,
    CONSTRUCTION_YEAR_ALIASES,
  );
  const mappedLessorType = mapToEnumValue(
    declaration.lessorType,
    LESSOR_VALUES,
    'Private',
    LESSOR_ALIASES,
  );
  const mappedDeductibleType = mapToEnumValue(
    declaration.deductibleType,
    DEDUCTIBLE_VALUES,
    DeductibleType.TroisCents,
    DEDUCTIBLE_ALIASES,
  );

  console.log('risk items', declaration);
  const riskItemHAB = {
    order: 1,
    typeCode: 'HAB',
    name: 'Habitation',
    fields: {
      ADR_RISQUE:
        declaration.address?.addressAutocomplete?.streetNumber &&
        declaration.address?.addressAutocomplete.street
          ? `${declaration.address.addressAutocomplete.streetNumber} ${declaration.address.addressAutocomplete.street}`
          : (declaration.address?.address ?? ''),
      ADR_CODE_POSTAL:
        declaration.address?.addressAutocomplete?.zipCode ?? declaration.address?.zipCode ?? '',
      ADR_COMP: '',
      ADR_VILLE: declaration.address?.addressAutocomplete?.city ?? declaration.address?.city ?? '',
      ADR_ETAGE: mappedApartmentFloor,
      HAB_TYPE: mappedHousingType,
      NBR_PIECES_40M2: declaration.numberOfRooms?.between41And75 ?? 0,
      NATURE_LOG: mappedResidenceType,
      NATURE_OCCU: mappedQualiteOccupant,
      NBR_PIECES: declaration.numberOfRooms?.numberOfRooms ?? 0,
      SURFACE_DEP: declaration.dependances?.otherDependanceSurface ?? SurfaceDependance.Aucune,
      POSSEDE_ENERGIEV: declaration.durableDevelopmentInstallation ?? '',
      POSSEDE_VERANDA: declaration.numberOfRooms?.hasVeranda ?? false,
      POSSEDE_PISCINE: declaration.hasSwimmingPoolSpaOrJacuzzi ?? false,
      REV_FISCAL: declaration.revenus ?? '',
      TYPE_CH: SuperficieChambre.Unknown, //declaration.typeOfBedroom ?? SuperficieChambre.Unknown,
      NBR_COLOC: declaration.nbColoc ?? 0,
      NOM_BAILLEUR: '', // defined below
      CAP_MOB_BESOIN: getCapitalMobilierFromRiskItems(path, declaration) ?? '',
      OBJ_VAL_UNIT: !!declaration.capitalMobilier?.valuableObjects,
      CLIMATIC_PERILS: climatic,
      NBR_PIECES_76M2: declaration.numberOfRooms?.moreThan76 ?? 0,
      POSSEDE_GARAGE: declaration.dependances?.hasGarage ?? false,
      POSSEDE_CAVE_9M2: !!declaration.dependances?.hasBasement,
      OBJVAL:
        !isNil(declaration.objetValue) && declaration.objetValue !== 'string'
          ? declaration.objetValue
          : '',
      RPM:
        !isNil(declaration.movableHeritage) && declaration.movableHeritage !== 'string'
          ? declaration.movableHeritage
          : '',
      ANNEE_CONSTRUCTION: mappedConstructionYear,
      TYPE_BAILLEUR:
        mappedQualiteOccupant === QualiteOccupant.Locataire
          ? mappedLessorType
          : Lessor.Unknown,
      FRANCHISE:
        store.path() === PathType.CONFORT
          ? mappedDeductibleType
          : DeductibleType.CentCinquante,
    },
  };

  if (declaration.lessor && declaration.lessor.id !== LessorType.Other) {
    riskItemHAB.fields.NOM_BAILLEUR = declaration.lessor.name ?? '';
  }

  arrayOfRiskItems.push(riskItemHAB);
  const children = store.offer().options.schoolInsurance.children;
  children
    .filter((child: ChildInfo) => child.lastName && child.firstName && child.birthDate)
    .forEach((child: ChildInfo, index: number) => {
      const childRiskItem = {
        name: 'Enfant',
        typeCode: 'ENF',
        order: index + 2, // L'ordre commence à 2 car l'habitation est à 1
        fields: {
          NOM_ENFANT: child.lastName ?? '',
          PRENOM_ENFANT: child.firstName ?? '',
          DATE_ENFANT: child.birthDate ? format(child.birthDate, 'dd/MM/yyyy') : '',
        },
      };
      arrayOfRiskItems.push(childRiskItem);
    });

  return arrayOfRiskItems;
}

// Fonction qui génère le DeclarationState avec `QuotationSummaryResponse` passé en paramètre
function getDeclarationStateFromQuotationSummary(
  quotationSummary: QuotationSummaryResponse,
): DeclarationState {
  const getRiskItemValue = (code: string): string | null =>
    quotationSummary.riskItemInformations.values.find((v) => v.code === code)?.value ?? null;

  const residenceType = mapToEnumValue(
    getRiskItemValue(NATURE_LOG),
    RESIDENCE_TYPE_VALUES,
    NatureResidence.Inconnue,
    RESIDENCE_TYPE_ALIASES,
  );
  const housingType = mapToEnumValue(
    getRiskItemValue(HAB_TYPE),
    HOUSING_TYPE_VALUES,
    TypeHabitation.Appartement,
    HOUSING_TYPE_ALIASES,
  );
  const apartmentFloor = mapToEnumValue(
    getRiskItemValue(ADR_ETAGE),
    APARTMENT_FLOOR_VALUES,
    Etage.Intermediaire,
    APARTMENT_FLOOR_ALIASES,
  );
  const qualiteOccupant = mapToEnumValue(
    getRiskItemValue(NATURE_OCCU),
    OCCUPANT_QUALITY_VALUES,
    QualiteOccupant.Locataire,
    OCCUPANT_QUALITY_ALIASES,
  );
  const dateHouseBuilted = mapToEnumValue(
    getRiskItemValue('ANNEE_CONSTRUCTION'),
    CONSTRUCTION_YEAR_VALUES,
    YearHouseBuilded.Inconnue,
    CONSTRUCTION_YEAR_ALIASES,
  );
  const deductibleType = mapToEnumValue(
    getRiskItemValue(FRANCHISE),
    DEDUCTIBLE_VALUES,
    DeductibleType.TroisCents,
    DEDUCTIBLE_ALIASES,
  );
  const typeOfBedroom = mapToEnumValue(
    getRiskItemValue(TYPE_CH),
    ROOM_SIZE_VALUES,
    SuperficieChambre.Unknown,
  );
  const lessorType = mapToEnumValue(
    getRiskItemValue(TYPE_BAILLEUR),
    LESSOR_VALUES,
    'Social',
    LESSOR_ALIASES,
  );

  return {
    // name: quotationSummary.riskItemInformations.values.find(v => v.code === NOM_ANI)?.value ?? '',
    email: '',
    address: {
      addressAutocomplete: {
        streetNumber:
          quotationSummary.clientInformations.address1.split(' ').slice(0, 1).join(' ') ?? '',
        street: quotationSummary.clientInformations.address1.split(' ')[0] ?? '',
        zipCode: quotationSummary.clientInformations.zipCode,
        city: quotationSummary.clientInformations.addressCity,
      },
      address: quotationSummary.clientInformations.address1,
      city: quotationSummary.clientInformations.addressCity,
      country: null,
      zipCode: quotationSummary.clientInformations.zipCode,
    },
    residenceType,
    housingType,
    dateHouseBuilted,
    apartmentFloor,
    qualiteOccupant,
    nbColoc: Number(
      quotationSummary.riskItemInformations.values.find((v) => v.code === NBR_COLOC)?.value ?? 0,
    ),
    deductibleType:
      quotationSummary.formulasAndOptionsInformations.formulaCode === PathType.CONFORT
        ? deductibleType
        : DeductibleType.CentCinquante,
    nbPersonInHome: null,
    colocInformations: null,
    typeOfBedroom,
    lessor: getRiskItemValue(NOM_BAILLEUR)
      ? {
          id: -2, // Assuming social if the lessor is not "Other"
          name: getRiskItemValue(NOM_BAILLEUR) ?? '',
          lessorType: 0,
          email: '',
        }
      : {
          id: -2, // Assuming social if the lessor is not "Other"
          name: 'Autre',
          lessorType: 0,
          email: '',
        },
    lessorType,
    subscriberHasBeenCanceled: null,
    susbcriberHadClaimsInLast3Years: null,
    numberOfRooms: {
      numberOfRooms: Number(
        quotationSummary.riskItemInformations.values.find((v) => v.code === NBR_PIECES)?.value ?? 0,
      ),
      between41And75: Number(
        quotationSummary.riskItemInformations.values.find((v) => v.code === NBR_PIECES_40M2)
          ?.value ?? 0,
      ),
      moreThan76: null,
      hasVeranda: null,
    },
    dependances: null,
    durableDevelopmentInstallation: null,
    hasSwimmingPoolSpaOrJacuzzi: !!quotationSummary.riskItemInformations.values.find(
      (v) => v.code === POSSEDE_PISCINE,
    )?.value,
    capitalMobilier: null,
    preferentialRate: null,
    effect_date: quotationSummary.requestedStartEffectiveDate
      ? format(
          parse(quotationSummary.requestedStartEffectiveDate, 'yyyy-MM-dd', new Date()),
          'dd/MM/yyyy HH:mm:ss',
        )
      : null,
    movableHeritage:
      quotationSummary.riskItemInformations.values.find((v) => v.code === RPM)?.value ?? '',
    objetValue:
      quotationSummary.riskItemInformations.values.find((v) => v.code === OBJVAL)?.value ?? '',
    revenus: '',
  };
}

// Fonction qui retourne un RiskItem spécifique
// eslint-disable-next-line @typescript-eslint/no-explicit-any
// export function getRiskItem(store: any, key: string): RiskItem | undefined {
//   const riskItems = getRiskItems(store);
//   return riskItems[key]; // Retourne l'objet si trouvé, sinon `undefined`
// }

export function getDeclarationState(quotationSummary: QuotationSummaryResponse): DeclarationState {
  return getDeclarationStateFromQuotationSummary(quotationSummary);
}

// function format_date(dateString: string): string {
//   let date: Date = new Date();
//   if (typeof dateString === "string") {
//     date = parse(dateString, "dd/MM/yyyy", new Date());
//   }

//   return format(date, "yyyy-MM-dd");
// }

/// Function that should return  "<20000" if the capital mobilier is less than 20000, "Entre 20000 et 50000" if it's between 20000

function getCapitalMobilierFromRiskItems(path: string, store: DeclarationState): string | null {
  let value = '';

  if (path === PathType.ETUDIANT) {
    // HANDLE CAPITAL MOBILIER FOR ETUDIANT PATH
  } else {
    value = store.capitalMobilier?.insuredValue ?? '';
  }

  return value;
}
